const STEPS = [
  {
    number: "01",
    icon: "ri-user-settings-line",
    title: "Build Your Capability Profile",
    description:
      "Skip the formal resume. Select what you can actually do — physical abilities, skills, tools you own, and tech comfort level.",
    highlight: "Takes under 3 minutes",
    color: "blue",
  },
  {
    number: "02",
    icon: "ri-ai-generate",
    title: "AI Matches You Instantly",
    description:
      "Our engine scans hundreds of local and remote opportunities, ranking them by pay rate, distance, and capability fit in real time.",
    highlight: "Average: 4 min to first match",
    color: "violet",
  },
  {
    number: "03",
    icon: "ri-file-text-line",
    title: "Apply with AI Assistance",
    description:
      "Auto-generated resumes and cover letters tailored per job. Review and confirm — we handle the rest. Or go live on the marketplace.",
    highlight: "1-click semi-automated apply",
    color: "green",
  },
  {
    number: "04",
    icon: "ri-money-dollar-circle-line",
    title: "Start Earning Today",
    description:
      "Accept same-day gigs, track earnings on your dashboard, and get upskill suggestions to unlock higher-paying roles over time.",
    highlight: "Same-day payout available",
    color: "orange",
  },
];

const colorMap: Record<string, { bg: string; text: string; border: string; num: string }> = {
  blue: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-100", num: "text-blue-200" },
  violet: { bg: "bg-violet-50", text: "text-violet-600", border: "border-violet-100", num: "text-violet-200" },
  green: { bg: "bg-green-50", text: "text-green-600", border: "border-green-100", num: "text-green-200" },
  orange: { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-100", num: "text-orange-200" },
};

export default function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full mb-4">
            Simple Process
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4">
            From Profile to Paycheck
            <br />
            <span className="text-blue-600">in Minutes</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            No long forms. No waiting weeks. Gig <span className="italic">n&apos;</span> Cash gets you earning faster than any platform on the market.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((step, i) => {
            const c = colorMap[step.color];
            return (
              <div
                key={step.number}
                className={`relative rounded-2xl border ${c.border} ${c.bg} p-6 flex flex-col gap-4 group hover:shadow-md transition-shadow duration-300`}
              >
                {/* Large number */}
                <span className={`absolute top-4 right-5 text-5xl font-black ${c.num} select-none`}>
                  {step.number}
                </span>
                {/* Icon */}
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-white border ${c.border}`}>
                  <i className={`${step.icon} text-2xl ${c.text}`} />
                </div>
                {/* Content */}
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">{step.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{step.description}</p>
                </div>
                {/* Highlight */}
                <div className={`mt-auto pt-3 border-t ${c.border}`}>
                  <span className={`text-xs font-semibold ${c.text} flex items-center gap-1.5`}>
                    <i className="ri-sparkling-line" />
                    {step.highlight}
                  </span>
                </div>

                {/* Connector arrow (not for last) */}
                {i < STEPS.length - 1 && (
                  <div className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center bg-white rounded-full border border-gray-200 shadow-sm">
                    <i className="ri-arrow-right-line text-gray-400 text-sm" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="text-white/70 text-sm font-semibold mb-1">Ready to get started?</div>
            <h3 className="text-white text-2xl md:text-3xl font-extrabold">Your first match is waiting.</h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                const el = document.getElementById("capabilities");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-7 py-3 bg-white text-blue-700 font-bold rounded-xl text-sm hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-flashlight-line mr-2" />
              Build My Profile
            </button>
            <button
              onClick={() => {
                const el = document.getElementById("pricing");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-7 py-3 bg-white/10 border border-white/20 text-white font-bold rounded-xl text-sm hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              View Pricing
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
